import { BodyMedium, HeadingLarge, theme } from '@arnold/common';
import { ReactNode, useMemo, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { TFunction, Trans, useTranslation } from 'react-i18next';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { GraphQLErrorCard, Loading } from '../../components';
import { PageWrapper } from '../../components/Common';
import { InfoMessage } from '../../components/Forms/RespondentForm/RespondentForm';
import {
  Features,
  OrganizationQuery,
  OrganizationTeamsQuery,
  ReportRecipientsQuery,
  useReportRecipientsQuery,
  UserQuery,
} from '../../generated/hooks';
import { useFeatureFlag } from '../../lib/helpers/hooks';
import { useGetOrganizationTeams } from '../Surveys/utils';
import { EmployeeDetail } from './EmployeeDetail';
import NoRespondents from './NoRespondents';
import { TeamDetail } from './TeamDetail';
import { MemberWithLeaderOfTeams, TeamsAndPeople, TeamsWithSubTeams, TeamWithSubTeams } from './TeamsAndPeople';

interface IProps extends RouteComponentProps<any> {
  user: UserQuery['user'];
  organization: OrganizationQuery['organization'];
}

interface ButtonProps {
  children: ReactNode;
  onClick: () => void;
  className?: string;
}
const AddRespondentButton = ({ children, onClick, className }: ButtonProps) => (
  <Button
    variant="outline-primary"
    onClick={onClick}
    className={className}
    data-icom={'btn-add-employee'}
    data-cy={'add-employee-button'}
  >
    {children}
  </Button>
);

const UpdateTeamButton = ({ children, className }: { children: ReactNode; className?: string }) => {
  const history = useHistory();

  const goToUpdateTeam = () => {
    history.push('/updateTeam');
  };

  return (
    <Button
      color="primary"
      onClick={goToUpdateTeam}
      className={className}
      data-cy="admin-update-teams-button"
      data-icom="btn-update-teams"
    >
      {children}
    </Button>
  );
};

const TeamsScreen = ({ user, organization, history }: IProps) => {
  const [t] = useTranslation(['teamsScreen', 'languageSelector', 'AuthorizedPage', 'confirmation']);
  const [editingRespondent, setEditingRespondent] = useState<MemberWithLeaderOfTeams | undefined | null>(undefined);
  const [defaultTeamId, setDefaultTeamId] = useState<string | undefined>(undefined);
  const [editingTeam, setEditingTeam] = useState<TeamWithSubTeams | undefined | null>(undefined);
  const [firstOpenModal, setFirstOpenModal] = useState<null | 'respondent' | 'team'>(null);
  const [defaultSupervisorTeamId, setDefaultSupervisorTeamId] = useState<string | undefined>(undefined);
  const [latestCreatedEmployeeId, setLatestCreatedEmployeeId] = useState<string | undefined>(undefined);
  const [externallySetTeamId, setExternallySetTeamId] = useState<((teamId: string) => void) | undefined>(undefined);

  // eslint-disable-next-line prefer-const
  let { organizationTeams, loadingOrError } = useGetOrganizationTeams(user && organization ? organization.id : '0');

  const isEmployeeManagementDisabled = useFeatureFlag(Features.DisableEmployeeManagement);

  const {
    data: reportRecipientsData,
    loading: reportRecipientsLoading,
    error: reportRecipientsError,
  } = useReportRecipientsQuery({
    variables: {
      organizationId: user && organization ? organization.id : '0',
    },
    skip: !user || !organization,
  });

  // Transform report recipients data into a Map with respondent ID as key and array of team names as value
  const reportRecipientsMap = useMemo(() => {
    if (!reportRecipientsData?.reportRecipients || !organizationTeams) {
      return new Map<string, string[]>();
    }

    return createReportRecipientsMap(reportRecipientsData.reportRecipients, organizationTeams, t);
  }, [reportRecipientsData, organizationTeams, t]);

  const rootTeamsWithSubTeams = useMemo(() => {
    if (!organizationTeams) {
      return [];
    }
    return constructTeamStructure(
      organizationTeams.map((team) => {
        if (!team.name) {
          return { ...team, name: t('teams:withoutLeader') };
        } else {
          return team;
        }
      }),
      t,
    );
  }, [organizationTeams, t]);

  const teamLeaderIds = (organizationTeams?.map((team) => team.leader?.id).filter(Boolean) as string[]) || [];
  const updateRespondent = (respondent: MemberWithLeaderOfTeams | null, teamId?: string) => {
    setEditingRespondent(respondent);
    setDefaultTeamId(teamId);
    if (!firstOpenModal) {
      setFirstOpenModal('respondent');
    }
  };

  const addRespondent = () => {
    setEditingRespondent(null);
    setDefaultTeamId(undefined);
    if (!firstOpenModal) {
      setFirstOpenModal('respondent');
    }
  };

  const createTeam = (supervisorTeamId: string) => {
    setEditingTeam(null);
    setDefaultSupervisorTeamId(supervisorTeamId);
    if (!firstOpenModal) {
      setFirstOpenModal('team');
    }
  };

  const updateTeam = (team: TeamWithSubTeams | null) => {
    setEditingTeam(team);
    setDefaultSupervisorTeamId(undefined);
    if (!firstOpenModal) {
      setFirstOpenModal('team');
    }
  };

  // When user clicks on "Create team" item in the respondent form team dropdown
  const onCreateTeamSelected = (externallySetTeamId: (teamId: string) => void) => {
    createTeam('');
    setExternallySetTeamId(() => externallySetTeamId);
  };

  if (loadingOrError || reportRecipientsLoading) {
    return loadingOrError || <Loading />;
  }
  if (reportRecipientsError) {
    return <GraphQLErrorCard />;
  }
  if (!user || !organization || !organizationTeams) {
    return null;
  }
  if (organizationTeams.length === 0) {
    return (
      <>
        <NoRespondents user={user} organization={organization}>
          <UpdateTeamButton>{t('addMultipleEmployees')}</UpdateTeamButton>
          <AddRespondentButton onClick={addRespondent}>{t('addOneEmployee')}</AddRespondentButton>
        </NoRespondents>
        {editingRespondent !== undefined && (
          <EmployeeDetail
            organization={organization}
            user={user}
            respondent={editingRespondent}
            defaultTeamId={defaultTeamId}
            onClose={() => {
              setEditingRespondent(undefined);
              setDefaultTeamId(undefined);
              if (firstOpenModal === 'respondent') {
                setFirstOpenModal(null);
              }
            }}
            setLatestCreatedEmployeeId={setLatestCreatedEmployeeId}
            wide={firstOpenModal === 'respondent'}
            onCreateTeamSelected={onCreateTeamSelected}
            disableCreatingTeam={firstOpenModal === 'team'}
          />
        )}
      </>
    );
  }

  const numberOfEmployees = rootTeamsWithSubTeams.reduce((acc, team) => acc + team.numberOfEmployees, 0);

  return (
    <>
      {editingRespondent !== undefined && (
        <EmployeeDetail
          organization={organization}
          user={user}
          respondent={editingRespondent}
          defaultTeamId={defaultTeamId}
          onClose={() => {
            setEditingRespondent(undefined);
            setDefaultTeamId(undefined);
            if (firstOpenModal === 'respondent') {
              setFirstOpenModal(null);
            }
          }}
          setLatestCreatedEmployeeId={setLatestCreatedEmployeeId}
          latestCreatedEmployeeId={latestCreatedEmployeeId}
          wide={firstOpenModal === 'respondent'}
          onCreateTeamSelected={onCreateTeamSelected}
          disableCreatingTeam={firstOpenModal === 'team'}
        />
      )}
      {editingTeam !== undefined && (
        <TeamDetail
          organization={organization}
          user={user}
          team={editingTeam}
          defaultSupervisorTeamId={defaultSupervisorTeamId}
          onClose={(teamId) => {
            setEditingTeam(undefined);
            setDefaultTeamId(undefined);
            if (firstOpenModal === 'team') {
              setFirstOpenModal(null);
            }
            if (externallySetTeamId && teamId) {
              externallySetTeamId(teamId);
              setExternallySetTeamId(undefined);
            }
          }}
          teams={rootTeamsWithSubTeams}
          teamNames={organizationTeams.filter((team) => team.id !== editingTeam?.id).map((team) => team.name!)}
          respondents={organizationTeams.flatMap((team) => team.members)}
          teamLeaderIds={teamLeaderIds}
          addRespondent={addRespondent}
          latestCreatedEmployeeId={latestCreatedEmployeeId}
          setLatestCreatedEmployeeId={setLatestCreatedEmployeeId}
          wide={firstOpenModal === 'team'}
          disableCreatingNewPerson={firstOpenModal === 'respondent'}
        />
      )}
      <PageWrapper data-cy="admin-page-content">
        <HeadingLarge margin={`${theme.spacing.h} 0 0`} data-icom="heading-teams">
          {t('AuthorizedPage:organizationalStructure')}
        </HeadingLarge>
        {isEmployeeManagementDisabled && (
          <InfoMessage className="mt-8 pb-3">
            <p>{t('apiOnlyUpdateMessage')}</p>
            {organization.lastSuccessfulApiUpdate && (
              <p>
                {t('lastSuccessfulApiUpdate')}{' '}
                {new Date(organization.lastSuccessfulApiUpdate).toLocaleString(undefined, {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            )}
            {organization.lastFailedApiUpdate &&
              new Date(organization.lastFailedApiUpdate).getTime() >
                new Date(organization.lastSuccessfulApiUpdate).getTime() && (
                <p>
                  {t('lastFailedApiUpdate')}{' '}
                  {new Date(organization.lastFailedApiUpdate).toLocaleString(undefined, {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              )}
          </InfoMessage>
        )}
        <Card className="d-flex flex-row justify-content-between align-items-center p-6 my-8">
          <BodyMedium>
            <Trans
              i18nKey="teamsScreen:numberOfEmployees"
              values={{ numberOfEmployees, orgName: organization.name }}
              components={[<strong data-cy="number-of-employees" />]}
            />
          </BodyMedium>
          {!isEmployeeManagementDisabled ? (
            <div className="d-flex flex-row">
              <UpdateTeamButton className="my-0">{t('updateTeam')}</UpdateTeamButton>
            </div>
          ) : null}
        </Card>

        <Card className="p-8">
          {
            <TeamsAndPeople
              teams={rootTeamsWithSubTeams}
              organizationName={organization.name}
              updateRespondent={updateRespondent}
              createTeam={createTeam}
              updateTeam={updateTeam}
              reportRecipients={reportRecipientsMap}
            />
          }
        </Card>
      </PageWrapper>
    </>
  );
};

export default TeamsScreen;

export const constructTeamStructure = (
  organizationTeams: NonNullable<OrganizationTeamsQuery['organizationTeams']>,
  t: TFunction,
  teamTransformer?: (team: TeamWithSubTeams) => TeamWithSubTeams,
): TeamsWithSubTeams => {
  let teams = organizationTeams.map((team) => ({
    ...team,
    members: team.members.map((member) => ({ ...member, leaderOfTeams: [] as TeamsWithSubTeams })),
  })) as TeamsWithSubTeams;

  if (teamTransformer) {
    teams = teams.map(teamTransformer);
  }

  // add leaderOfTeams property to each member
  const allMembers = teams.flatMap((team) => team.members);
  teams.forEach((team) => {
    const leader = allMembers.find((member) => member.id === team.leader?.id);
    leader?.leaderOfTeams && leader.leaderOfTeams.push(team);
  });

  // add subTeams to team
  teams.forEach((team) => {
    team.subTeams = teams.filter((subTeam) => team !== subTeam && team.id === subTeam.supervisorTeam?.id);
  });

  // find rootTeams
  const rootTeams = teams.filter((team) => team.rootTeam === true);
  // add numberOfEmployees to team and all subTeams
  rootTeams.forEach(addNumberOfEmployeesToTeam);

  // Helper function for sorting teams
  const sortTeamsByName = (a: TeamsWithSubTeams[0], b: TeamsWithSubTeams[0]) => {
    const withoutLeaderName = t('teams:withoutLeader');

    // Put teams with name "withoutLeader" last
    if (a.name === withoutLeaderName) return 1;
    if (b.name === withoutLeaderName) return -1;

    // Handle null/empty names
    if (a.name == null || a.name === '') return 1;
    if (b.name == null || b.name === '') return -1;

    // Normal alphabetical comparison
    return a.name.localeCompare(b.name);
  };

  // sort team members and subTeams alphabetically, teams without name are last
  teams.forEach((team) => {
    team.members = [...team.members].sort((a, b) => {
      if (a.surname == null || a.firstname == null) {
        return 1;
      }
      if (b.surname == null || b.firstname == null) {
        return -1;
      }
      if (a.surname === b.surname) {
        return a.firstname.localeCompare(b.firstname);
      }
      return a.surname.localeCompare(b.surname);
    });
    team.subTeams.sort(sortTeamsByName);
  });

  // Sort root teams using the same function
  rootTeams.sort(sortTeamsByName);

  return rootTeams;
};

/**
 * add numberOfEmployees to team and all subTeams
 * also counting the number of employees in subTeams
 */
const addNumberOfEmployeesToTeam = (team: TeamsWithSubTeams[0]) => {
  team.numberOfEmployees = team.members.length;
  team.subTeams.forEach((subTeam) => {
    addNumberOfEmployeesToTeam(subTeam);
    team.numberOfEmployees += subTeam.numberOfEmployees;
  });
};

/**
 * Creates a map of respondent IDs to team names they have report access for.
 * A respondent has access to all teams if either `team == null` or they have report recipient records for ALL root teams in the organization
 *
 * If a respondent has access to all teams, they will only have a single entry with the "allTeams" label.
 */
const createReportRecipientsMap = (
  reportRecipients: ReportRecipientsQuery['reportRecipients'],
  organizationTeams: NonNullable<OrganizationTeamsQuery['organizationTeams']>,
  t: TFunction,
): Map<string, string[]> => {
  const map = new Map<string, string[]>();

  const rootTeamIds = new Set<string>(
    organizationTeams.filter((team) => team.rootTeam === true).map((team) => team.id),
  );

  // First process all recipients to identify those with team == null (all teams access) - legacy behavior
  const allTeamsAccessIds = new Set<string>();

  reportRecipients.forEach((recipient) => {
    if (recipient.respondent && !recipient.respondentDeleted && !recipient.teamDeleted && recipient.team == null) {
      const respondentId = recipient.respondent.id;
      allTeamsAccessIds.add(respondentId);

      // Set the special "all teams" label and clear any previous entries
      map.set(respondentId, [t('updateRespondent:allTeams')]);
    }
  });

  // Then identify respondents who have access to all teams by having report recipient records
  // for ALL root teams (current behavior). Skip respondents already identified via legacy behavior.
  if (rootTeamIds.size > 0) {
    const respondentRootTeamIds = new Map<string, Set<string>>();

    reportRecipients.forEach((recipient) => {
      if (recipient.respondent && !recipient.respondentDeleted && !recipient.teamDeleted && recipient.team != null) {
        const respondentId = recipient.respondent.id;

        if (allTeamsAccessIds.has(respondentId)) {
          return;
        }

        const teamId = recipient.team.id;
        if (!rootTeamIds.has(teamId)) {
          return;
        }

        if (!respondentRootTeamIds.has(respondentId)) {
          respondentRootTeamIds.set(respondentId, new Set<string>());
        }

        respondentRootTeamIds.get(respondentId)!.add(teamId);
      }
    });

    respondentRootTeamIds.forEach((ids, respondentId) => {
      if (ids.size === rootTeamIds.size) {
        allTeamsAccessIds.add(respondentId);
        map.set(respondentId, [t('updateRespondent:allTeams')]);
      }
    });
  }

  // Finally process all team-specific recipients (skip if respondent already has all teams access)
  reportRecipients.forEach((recipient) => {
    if (recipient.respondent && !recipient.respondentDeleted && !recipient.teamDeleted && recipient.team != null) {
      const respondentId = recipient.respondent.id;

      // Skip if this respondent already has all teams access
      if (allTeamsAccessIds.has(respondentId)) {
        return;
      }

      const teamName = (recipient.team.name || t('teams:withoutLeader'))!;

      if (!map.has(respondentId)) {
        map.set(respondentId, []);
      }

      const existing = map.get(respondentId)!;
      if (!existing.includes(teamName)) {
        existing.push(teamName);
      }
    }
  });

  return map;
};
